import { useState, useEffect } from 'react';
import { useRole } from './useRole';
import { useToast } from '@/components/ui/use-toast';
import { 
  fetchPlayerProfile, 
  fetchSocialMedia, 
  updatePlayerProfile,
  updateSocialMedia,
  uploadProfileImage
} from '@/services/playerProfileService';
import { Profile, SocialMedia, ProfileUpdate, SocialMediaUpdate } from '@/types/playerProfile';

export function usePlayerProfile() {
  const { user, authUser } = useRole();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [socialMedia, setSocialMedia] = useState<SocialMedia | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const { toast } = useToast();

  useEffect(() => {
    const fetchData = async () => {
      if (!authUser?.id) return;

      try {
        setLoading(true);
        
        // Always fetch fresh profile data to ensure we have latest nickname and preferred_game
        console.log('🔍 Carregando dados do perfil do jogador...');
        const profileData = await fetchPlayerProfile(authUser.id);
        console.log('✅ Dados do perfil carregados:', profileData);
        setProfile(profileData);
        
        // Next, get the social media data
        try {
          console.log('🔍 Carregando dados de redes sociais...');
          const socialData = await fetchSocialMedia(authUser.id);
          console.log('✅ Dados de redes sociais carregados:', socialData);
          setSocialMedia(socialData);
        } catch (error) {
          console.error('Error fetching social media:', error);
          // Set default empty values for social media if there's still an error
          setSocialMedia({
            id: '',
            biography: '',
            instagram_url: '',
            discord_url: '',
            twitch_url: '',
            youtube_url: '',
            image_url: '',
            cover_url: ''
          });
        }
      } catch (error) {
        console.error('Error in usePlayerProfile:', error);
        toast({
          title: "Erro ao carregar perfil",
          description: "Não foi possível carregar os dados do perfil.",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [authUser?.id, toast]);

  const handleUpdateProfile = async (profileUpdate: ProfileUpdate) => {
    if (!authUser?.id || !profile) return false;
    
    try {
      await updatePlayerProfile(authUser.id, profileUpdate);
      setProfile({ ...profile, ...profileUpdate });
      return true;
    } catch (error) {
      toast({
        title: "Erro ao atualizar perfil",
        description: "Não foi possível salvar as alterações.",
        variant: "destructive"
      });
      throw error;
    }
  };
  
  const handleUpdateSocialMedia = async (socialUpdate: SocialMediaUpdate) => {
    if (!authUser?.id) return false;
    
    try {
      const updatedSocialMedia = await updateSocialMedia(authUser.id, socialUpdate, socialMedia);
      console.log('✅ [usePlayerProfile] Social media atualizado no banco:', updatedSocialMedia);
      setSocialMedia(updatedSocialMedia);
      return true;
    } catch (error) {
      console.error('❌ [usePlayerProfile] Erro ao atualizar social media:', error);
      
      // Se falhou na API mas temos uma URL de imagem válida, ainda vamos atualizar o estado local
      if (socialUpdate.image_url) {
        console.log('🔄 [usePlayerProfile] Atualizando estado local apesar do erro da API');
        setSocialMedia(prev => prev ? { ...prev, ...socialUpdate } : {
          id: '',
          biography: '',
          instagram_url: '',
          discord_url: '',
          twitch_url: '',
          youtube_url: '',
          image_url: socialUpdate.image_url || '',
          cover_url: ''
        });
      }
      
      toast({
        title: "Erro ao atualizar perfil",
        description: "Não foi possível salvar as alterações nas redes sociais.",
        variant: "destructive"
      });
      throw error;
    }
  };

  const handleUploadProfileImage = async (file: File) => {
    if (!authUser?.id) {
      toast({
        title: "Erro de autenticação",
        description: "Você precisa estar logado para fazer upload de imagens.",
        variant: "destructive"
      });
      return null;
    }
    
    try {
      console.log('🔄 [usePlayerProfile] Iniciando upload de imagem...');
      const publicUrl = await uploadProfileImage(authUser.id, file);
      console.log('✅ [usePlayerProfile] Upload concluído, atualizando perfil...');
      
      // Update the social media record with the new image URL
      await handleUpdateSocialMedia({ image_url: publicUrl });
      console.log('✅ [usePlayerProfile] Perfil atualizado com nova imagem');
      
      // Force immediate state update to ensure the sidebar shows the new image
      setSocialMedia(prev => prev ? { ...prev, image_url: publicUrl } : {
        id: '',
        biography: '',
        instagram_url: '',
        discord_url: '',
        twitch_url: '',
        youtube_url: '',
        image_url: publicUrl,
        cover_url: ''
      });
      
      console.log('✅ [usePlayerProfile] Estado local atualizado com nova imagem:', publicUrl);
      
      return publicUrl;
    } catch (error) {
      console.error('❌ [usePlayerProfile] Erro no upload:', error);
      
      // Mensagem de erro mais específica baseada no tipo de erro
      let errorMessage = "Não foi possível fazer o upload da imagem.";
      
      if (error instanceof Error) {
        if (error.message.includes('storage')) {
          errorMessage = "Problema no sistema de armazenamento. Tente novamente em alguns instantes.";
        } else if (error.message.includes('tamanho') || error.message.includes('5MB')) {
          errorMessage = "A imagem é muito grande. Use uma imagem de até 5MB.";
        } else if (error.message.includes('arquivo de imagem')) {
          errorMessage = "Por favor, selecione um arquivo de imagem válido (JPG, PNG, etc).";
        } else if (error.message.includes('configurado')) {
          errorMessage = "Sistema de upload não configurado. Entre em contato com o suporte.";
        } else {
          errorMessage = error.message;
        }
      }
      
      toast({
        title: "Erro ao fazer upload",
        description: errorMessage,
        variant: "destructive"
      });
      
      return null;
    }
  };

  const handleRemoveProfileImage = async () => {
    if (!authUser?.id || !socialMedia?.image_url) return false;
    
    try {
      // Update the social media record to remove the image URL
      await handleUpdateSocialMedia({ image_url: null });
      return true;
    } catch (error) {
      toast({
        title: "Erro ao remover imagem",
        description: "Não foi possível remover a imagem do perfil.",
        variant: "destructive"
      });
      return false;
    }
  };

  return { 
    profile, 
    socialMedia, 
    loading, 
    updateProfile: handleUpdateProfile, 
    updateSocialMedia: handleUpdateSocialMedia,
    uploadProfileImage: handleUploadProfileImage,
    removeProfileImage: handleRemoveProfileImage
  };
}
